import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import type { Photo } from './AevocamViewer'
import { imagePrefetch, type ImagePrefetchImages, type ImagePrefetchLoaded } from './imagePrefetch'
import { useAevocamPlayer } from './useAevocamPlayer'

//------------------------------------------------------------
// AevocamHighRes component - handles full-resolution image display
//------------------------------------------------------------
type AevocamHighResProps = {
  photos: (Photo | undefined)[]
  viewWidth: number
  viewHeight: number
  highGear: boolean
  token: string
}

const MAX_CONCURRENT_LOADING = 5
const LOOK_AHEAD_IN_HIGH_GEAR = 15

export function AevocamHighRes({
  photos,
  viewWidth,
  viewHeight,
  highGear,
  token,
}: AevocamHighResProps) {
  const { tenantId, feedId, currentIndex, playReverse, cdnUrl, proactiveLoading } = useAevocamPlayer()

  const [showFull, setShowFull] = useState(true)
  const [photosLoaded, setPhotosLoaded] = useState<ImagePrefetchLoaded>({})
  
  // Iteration ref for bailing out of prefetch when props change
  const prefetchIteration = useRef({ current: 0 })
  
  // Counter for concurrent image loading for this component
  const activelyLoadingRef = useRef({ current: 0 })

  const currentPhoto = photos[currentIndex]

  //------------------------------------------------------------
  // Convert photos to ImagePrefetchImages format
  const prefetchImages: ImagePrefetchImages = useMemo(() => {
    const base = (s: number) =>
      `${cdnUrl}/${tenantId}/f/${feedId}/photo/${s}`
    return photos.map((photo, index) => ({
      key: String(index),
      url: `${base(photo!.s)}?tk=${encodeURIComponent(token)}`,
    }))
  }, [photos, tenantId, feedId, cdnUrl, token])

  //------------------------------------------------------------
  // Debounce showFull - only show full-res image after currentIndex settles
  useEffect(() => {
    setShowFull(true)
    if (highGear) return
    setShowFull(false)
    const timer = setTimeout(() => {
      setShowFull(true)
    }, 150)
    return () => clearTimeout(timer)
  }, [currentIndex, highGear])

  //------------------------------------------------------------
  // Set showFull to true when switching into low gear
  useEffect(() => {
    if (highGear) {
      setShowFull(true)
    }
  }, [highGear])

  //------------------------------------------------------------
  // Prefetch images using imagePrefetch function
  useEffect(() => {
    // Don't prefetch when user has been inactive (saves loading the full image set)
    if (!proactiveLoading) return
    // Only prefetch when sprites are in low gear (highGear prop is true means sprites are in low gear)
    if (!highGear) return

    // Increment iteration to cancel any previous prefetch
    prefetchIteration.current.current++

    // Start prefetching from currentIndex in the playback direction
    imagePrefetch({
      iteration: prefetchIteration.current,
      activelyLoading: activelyLoadingRef.current,
      startAtIndex: currentIndex,
      reverse: playReverse,
      simultaneous: MAX_CONCURRENT_LOADING,
      lookAhead: LOOK_AHEAD_IN_HIGH_GEAR,
      images: prefetchImages,
      loaded: photosLoaded,
      setLoaded: setPhotosLoaded,
      setHighGear: () => {}, // High-res doesn't need to report its gear state
    })

    // Cleanup: increment iteration to cancel prefetch on unmount or prop change
    return () => {
      prefetchIteration.current.current++
    }
  }, [proactiveLoading, highGear, currentIndex, playReverse, prefetchImages])

  //------------------------------------------------------------
  const handleLoad = useCallback((ev: React.SyntheticEvent<HTMLImageElement>) => {
    const el = ev.target as HTMLImageElement;
    el.setAttribute('data-loaded', 'true');
  }, [currentIndex])

  //------------------------------------------------------------
  const handleError = useCallback((ev: React.SyntheticEvent<HTMLImageElement>) => {
    const el = ev.target as HTMLImageElement;
    el.setAttribute('data-loaded', 'false');
  }, [currentIndex])

  //------------------------------------------------------------  
  if (!currentPhoto) {
    return <></>
  }

  //------------------------------------------------------------
  // Render
  return (
    <div className='AevocamHighRes'>
      {photosLoaded[String(currentIndex)] && (
        <img
          key={currentIndex}
          alt='High-res version'
          src={`${cdnUrl}/${tenantId}/f/${feedId}/photo/${currentPhoto.s}?tk=${encodeURIComponent(token)}`}
          style={{
            width: viewWidth,
            height: viewHeight,
          }}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
    </div>
  )
}

